/**
 * Global Notepad popup for the SHSAT Adaptive test-taking page.
 *
 * Mirrors the legacy ``Module/index.tsx`` notepad (lines 1719-1735): a
 * draggable popup pinned inside the ``digitalSHSATModule`` wrapper,
 * with a header (drag handle + close button) and a textarea body.
 *
 * Persistence: legacy persists notes via ``autoSave`` (server-side).
 * The adaptive backend doesn't have a notes endpoint yet (no batched
 * save model — every slot is committed individually), so notes are
 * persisted to localStorage keyed by studentTestId. Survives reload;
 * cleared when the student finishes and submits the test.
 *
 * Visibility is driven by ``isOpen`` so the legacy CSS class swap
 * (``--notepad`` vs ``--hidden``) animates in/out the same way.
 */

import React, { useEffect, useRef, useState } from 'react';
import { IoClose } from 'react-icons/io5';

import { attachDragMove } from './dragMove';


function storageKey(studentTestId: string): string {
  return `shsatAdaptive:notepad:${studentTestId}`;
}


export function GlobalNotepad(props: {
  isOpen: boolean;
  studentTestId: string;
  onClose: () => void;
}): JSX.Element {
  const { isOpen, studentTestId, onClose } = props;
  const dragRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [notes, setNotes] = useState<string>(() => {
    try {
      return localStorage.getItem(storageKey(studentTestId)) ?? '';
    } catch {
      return '';
    }
  });

  // Drag move: attach mouse/touch handlers when the popup is mounted.
  // Parent is the ``digitalSHSATModule`` wrapper so the popup is
  // clamped within the module bounds (legacy semantics).
  useEffect(() => {
    if (!isOpen) return;
    const el = dragRef.current;
    const handle = headerRef.current;
    const parent = document.getElementById('digitalSHSATModule');
    if (!el || !handle || !parent) return;
    return attachDragMove(el, handle, parent);
  }, [isOpen]);

  // Persist on change. Debouncing is not necessary at human typing
  // speeds; localStorage writes are synchronous but cheap (~µs).
  const handleChange: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    const value = e.target.value;
    setNotes(value);
    try {
      localStorage.setItem(storageKey(studentTestId), value);
    } catch {
      // private-browsing: notes still work in-memory for this session
    }
  };

  return (
    <div
      id="notepadDrag"
      ref={dragRef}
      className={
        isOpen
          ? 'digitalSHSATModule__popup--notepad'
          : 'digitalSHSATModule__popup--hidden'
      }
    >
      <div
        id="notepadHeader"
        ref={headerRef}
        className="digitalSHSATModule__popupHeader draggable-element"
      >
        <h3 className="digitalSHSATModule__popupHeaderText">Global Notepad</h3>
        <IoClose
          className="digitalSHSATModule__popupHeaderClose"
          onClick={onClose}
        />
      </div>
      <div className="digitalSHSATModule__popupNotepad">
        <textarea
          className="digitalSHSATModule__popupNotepadInput"
          value={notes}
          onChange={handleChange}
        />
      </div>
    </div>
  );
}
